import React from 'react';
import { Link } from 'react-router-dom';
import { Layers, Zap, Settings, CheckCircle, BarChart3, Lock } from 'lucide-react';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const DeepFlow = () => {
  const heroRef = useScrollAnimation({ threshold: 0.2 });
  const statsRef = useScrollAnimation({ threshold: 0.2 });
  const featuresRef = useScrollAnimation({ threshold: 0.2 });
  const useCasesRef = useScrollAnimation({ threshold: 0.2 });

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="bg-gradient-to-br from-red-50 to-red-100 py-20 lg:py-28 overflow-hidden" ref={heroRef.ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-12 items-center">
            <div className="max-w-2xl">
              <motion.div
                className="inline-flex items-center justify-center p-3 bg-red-100 rounded-2xl mb-6"
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, ease: "backOut" }}
                viewport={{ once: true, margin: "-100px" }}
              >
                <Layers className="text-red-600" size={32} />
              </motion.div>
              <motion.h1
                className="text-4xl md:text-6xl font-bold text-gray-900 mb-6"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
                viewport={{ once: true, margin: "-100px" }}
              >
                DeepFlow AI
              </motion.h1>
              <motion.p
                className="text-xl text-gray-600 mb-8 leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                viewport={{ once: true, margin: "-100px" }}
              >
                Universal workflow automation platform that transforms document-heavy business processes into organized, intelligent workflows across any industry. Email processing, intelligent document extraction, and AI-powered case management.
              </motion.p>
              <motion.div
                className="flex flex-col sm:flex-row gap-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
                viewport={{ once: true, margin: "-100px" }}
              >
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link to="/contact" className="px-8 py-3 bg-cyan-500 text-white rounded-full font-semibold hover:bg-cyan-600 transition-colors text-center inline-block">
                    Request Demo
                  </Link>
                </motion.div>
              </motion.div>
            </div>
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-cyan-400 to-red-500 rounded-3xl rotate-3 opacity-10"
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.img
                src="/deepflowimage.webp"
                alt="Flexible Workflow Architecture"
                className="relative rounded-3xl shadow-2xl border-4 border-white"
                whileHover={{ y: -8, scale: 1.02 }}
                transition={{ duration: 0.3 }}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-white border-b border-gray-100" ref={statsRef.ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            {[
              { stat: '50-70%', label: 'Reduction in Processing Time', color: 'bg-cyan-50 hover:bg-cyan-100', textColor: 'text-cyan-600' },
              { stat: '80-90%', label: 'Manual Data Entry Eliminated', color: 'bg-red-50 hover:bg-red-100', textColor: 'text-red-600' },
              { stat: '3-6 months', label: 'ROI Payback Period', color: 'bg-blue-50 hover:bg-blue-100', textColor: 'text-blue-600' },
            ].map((item, idx) => (
              <motion.div
                key={idx}
                className={`p-6 rounded-xl ${item.color} transition-all duration-300`}
                initial={{ opacity: 0, y: 40, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.6, delay: idx * 0.1, ease: "easeOut" }}
                viewport={{ once: true, margin: "-100px" }}
                whileHover={{ y: -4, scale: 1.02 }}
              >
                <motion.div
                  className={`text-4xl font-bold ${item.textColor} mb-2`}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.6, delay: idx * 0.1 + 0.2 }}
                  viewport={{ once: true }}
                >
                  {item.stat}
                </motion.div>
                <motion.div
                  className="text-gray-600"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.6, delay: idx * 0.1 + 0.3 }}
                  viewport={{ once: true }}
                >
                  {item.label}
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section className="py-24" ref={featuresRef.ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2
              className="text-3xl font-bold text-gray-900 mb-4"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              viewport={{ once: true, margin: "-100px" }}
            >
              Core Platform Capabilities
            </motion.h2>
            <motion.p
              className="text-lg text-gray-600"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
              viewport={{ once: true, margin: "-100px" }}
            >
              Universal workflow automation for any industry. Turn chaotic document-heavy processes into organized, intelligent workflows.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Layers, title: 'Email Intelligence', desc: 'Connect Gmail, Outlook, and IMAP. AI automatically categorizes emails, routes them to the right teams, and creates actionable cases from incoming messages.', color: 'cyan' },
              { icon: Settings, title: 'Multi-Format Document Processing', desc: 'Extract text and data from PDFs, Word documents, images, and scanned files. Advanced OCR with spatial understanding for accurate information extraction.', color: 'red' },
              { icon: Zap, title: 'AI Agent Orchestration', desc: 'Specialized AI agents that work autonomously on complex tasks. They retain context across conversations and integrate with databases and systems.', color: 'blue' },
            ].map((feature, idx) => {
              const Icon = feature.icon;
              const borderColor = feature.color === 'cyan' ? 'hover:border-cyan-300' : feature.color === 'red' ? 'hover:border-red-300' : 'hover:border-blue-300';
              const bgColor = feature.color === 'cyan' ? 'bg-cyan-100' : feature.color === 'red' ? 'bg-red-100' : 'bg-blue-100';
              const textColor = feature.color === 'cyan' ? 'text-cyan-600' : feature.color === 'red' ? 'text-red-600' : 'text-blue-600';
              return (
                <motion.div
                  key={idx}
                  className={`p-8 border border-transparent ${borderColor} rounded-2xl bg-white shadow-sm hover:shadow-xl transition-all duration-300`}
                  initial={{ opacity: 0, y: 40, rotateX: -10 }}
                  whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
                  transition={{ duration: 0.7, delay: idx * 0.15, ease: "easeOut" }}
                  viewport={{ once: true, margin: "-100px" }}
                  whileHover={{ y: -8, scale: 1.02 }}
                >
                  <motion.div
                    className={`w-12 h-12 ${bgColor} rounded-xl flex items-center justify-center ${textColor} mb-6`}
                    animate={{ y: [0, -5, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    whileHover={{ scale: 1.2, rotate: 10 }}
                  >
                    <Icon size={24} />
                  </motion.div>
                  <motion.h3
                    className="text-xl font-bold text-gray-900 mb-3"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 0.7, delay: idx * 0.15 + 0.1 }}
                    viewport={{ once: true }}
                  >
                    {feature.title}
                  </motion.h3>
                  <motion.p
                    className="text-gray-600"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 0.7, delay: idx * 0.15 + 0.2 }}
                    viewport={{ once: true }}
                  >
                    {feature.desc}
                  </motion.p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-24 bg-gray-50" ref={useCasesRef.ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2
              className="text-3xl font-bold text-gray-900 mb-4"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              viewport={{ once: true, margin: "-100px" }}
            >
              Industry Use Cases
            </motion.h2>
            <motion.p
              className="text-lg text-gray-600"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
              viewport={{ once: true, margin: "-100px" }}
            >
              Works across financial services, insurance, legal, government, healthcare, real estate, and more.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              { icon: CheckCircle, title: 'Mortgage Processing', desc: 'Automate loan applications with AI. Extract borrower info, verify income, check credit scores, and validate appraisals. Process 50-100 applications per day with 70% less manual work.', borderColor: 'hover:border-cyan-300', bgColor: 'bg-cyan-100', textColor: 'text-cyan-600' },
              { icon: BarChart3, title: 'Insurance Claims', desc: 'Process 200-500 claims daily. AI validates policies, detects fraud, organizes medical records, and auto-approves low-risk claims. 60% faster processing.', borderColor: 'hover:border-red-300', bgColor: 'bg-red-100', textColor: 'text-red-600' },
              { icon: Lock, title: 'Tender & RFP Management', desc: 'Evaluate 20-50 RFPs monthly with AI. Extract requirements, match against capabilities, score bids, and accelerate bid/no-bid decisions by 50%.', borderColor: 'hover:border-blue-300', bgColor: 'bg-blue-100', textColor: 'text-blue-600' },
              { icon: Zap, title: 'Legal Case Management', desc: 'Organize client emails, discovery documents, and deadlines. Paralegals spend 80% less time organizing files. Track billable hours automatically.', borderColor: 'hover:border-purple-300', bgColor: 'bg-purple-100', textColor: 'text-purple-600' },
              { icon: Lock, title: 'Government Procurement', desc: 'Handle 100+ vendor inquiries with full transparency. Validate compliance, score bids, and generate audit trails. 40% faster procurement cycle.', borderColor: 'hover:border-gray-300', bgColor: 'bg-gray-200', textColor: 'text-gray-700' },
              { icon: CheckCircle, title: 'Medical Records & Healthcare', desc: 'Process 500+ records requests per week with HIPAA compliance. Auto-validate authorizations and redact sensitive info. 70% faster turnaround.', borderColor: 'hover:border-cyan-400', bgColor: 'bg-cyan-200', textColor: 'text-cyan-700' },
            ].map((useCase, idx) => {
              const Icon = useCase.icon;
              return (
                <motion.div
                  key={idx}
                  className={`bg-white p-8 rounded-2xl shadow-sm border border-transparent ${useCase.borderColor} transition-all duration-300`}
                  initial={{ opacity: 0, x: idx % 2 === 0 ? -40 : 40, rotateY: idx % 2 === 0 ? 10 : -10 }}
                  whileInView={{ opacity: 1, x: 0, rotateY: 0 }}
                  transition={{ duration: 0.7, delay: idx * 0.1, ease: "easeOut" }}
                  viewport={{ once: true, margin: "-100px" }}
                  whileHover={{ y: -6, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)" }}
                >
                  <motion.div
                    className={`w-12 h-12 ${useCase.bgColor} rounded-xl flex items-center justify-center ${useCase.textColor} mb-6`}
                    whileHover={{ scale: 1.3, rotate: 360 }}
                    transition={{ duration: 0.5 }}
                  >
                    <Icon size={24} />
                  </motion.div>
                  <motion.h3
                    className="text-xl font-bold text-gray-900 mb-3"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 0.7, delay: idx * 0.1 + 0.1 }}
                    viewport={{ once: true }}
                  >
                    {useCase.title}
                  </motion.h3>
                  <motion.p
                    className="text-gray-600"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 0.7, delay: idx * 0.1 + 0.2 }}
                    viewport={{ once: true }}
                  >
                    {useCase.desc}
                  </motion.p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* From Legacy Processes to Smart Workflows */}
      <section className="py-20 relative overflow-hidden" style={{
        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(168, 85, 247, 0.05) 100%)',
      }}>
        {/* Animated shimmer effect */}
        <motion.div
          className="absolute -top-1/2 left-1/3 w-full h-full rounded-full blur-3xl opacity-30"
          style={{
            background: 'radial-gradient(circle, rgba(168, 85, 247, 0.15) 0%, transparent 70%)',
          }}
          animate={{
            x: [0, 50, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.h2
            className="text-4xl md:text-5xl font-bold text-gray-900 text-center mb-4"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            viewport={{ once: true, margin: "-100px" }}
          >
            Platform Features
          </motion.h2>
          <motion.div
            className="flex justify-center mb-16"
            initial={{ opacity: 0, scaleX: 0 }}
            whileInView={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-cyan-500 to-transparent"></div>
          </motion.div>

          {/* Vertical Timeline */}
          <div className="relative">
            {/* Central line - Hidden on mobile, shown on md+ */}
            <motion.div
              className="hidden md:block absolute left-1/2 top-0 bottom-0 w-1 -translate-x-1/2 bg-gradient-to-b from-cyan-500 via-purple-500 to-cyan-500"
              initial={{ scaleY: 0, originY: 0 }}
              whileInView={{ scaleY: 1 }}
              transition={{ duration: 1.8, ease: "easeOut" }}
              viewport={{ once: true, amount: "some", margin: "-200px" }}
            />

            {/* Mobile Timeline Line - Left aligned, shown only on mobile */}
            <div className="md:hidden absolute left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-cyan-500 via-purple-500 to-cyan-500"></div>

            {/* Timeline items */}
            <div className="space-y-8 md:space-y-16 relative">
              {[
                {
                  title: 'Smart Case Management',
                  description: 'Auto-create cases from emails and documents. Link related items, organize by priority, and track SLA compliance in real-time.',
                  color: 'bg-blue-500',
                  align: 'left',
                },
                {
                  title: 'Intelligent Routing',
                  description: 'AI automatically routes work to the right teams based on content, skills, and capacity. No manual assignment needed.',
                  color: 'bg-purple-500',
                  align: 'right',
                },
                {
                  title: 'Real-Time Analytics',
                  description: 'Dashboard with KPIs, workflow bottlenecks, team productivity, and compliance metrics. AI-driven recommendations.',
                  color: 'bg-orange-500',
                  align: 'left',
                },
                {
                  title: 'Team Collaboration',
                  description: 'Department-based organization with role-based access. Activity feeds, task assignments, and complete audit trails.',
                  color: 'bg-green-500',
                  align: 'right',
                },
                {
                  title: 'Industry Templates',
                  description: 'Pre-configured for 8+ industries: financial services, insurance, legal, government, healthcare, and more. Deploy in weeks.',
                  color: 'bg-pink-500',
                  align: 'left',
                },
                {
                  title: 'Enterprise Ready',
                  description: 'Cloud-native, multi-tenant SaaS. SSO, MFA, HIPAA-compliant, SOC 2 certified. Scale from 5 to 50,000 cases per day.',
                  color: 'bg-cyan-500',
                  align: 'right',
                },
              ].map((feature, idx) => (
                <div key={idx}>
                  {/* Mobile Layout */}
                  <motion.div
                    className="flex md:hidden items-start gap-4 pl-2"
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 + idx * 0.1, ease: "easeOut" }}
                    viewport={{ once: true, margin: "-50px" }}
                  >
                    {/* Mobile: Timeline dot */}
                    <div className={`w-5 h-5 rounded-full border-3 border-white ${feature.color} flex-shrink-0 flex items-center justify-center mt-1 shadow-md`}>
                      <div className="w-1.5 h-1.5 rounded-full bg-white" />
                    </div>
                    {/* Mobile: Card */}
                    <div className="flex-1">
                      <div className="bg-white rounded-xl p-4 shadow-md">
                        <h3 className="text-base font-bold text-gray-900 mb-1">{feature.title}</h3>
                        <p className="text-gray-600 text-xs leading-relaxed">{feature.description}</p>
                      </div>
                    </div>
                  </motion.div>

                  {/* Desktop Layout */}
                  <motion.div
                    className={`hidden md:flex relative items-center ${feature.align === 'left' ? 'flex-row' : 'flex-row-reverse'}`}
                    initial={{ opacity: 0, x: feature.align === 'left' ? -60 : 60 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 1.2 + idx * 0.12, ease: "easeOut" }}
                    viewport={{ once: true, margin: "-100px" }}
                  >
                    {/* Content */}
                    <div className={`w-1/2 ${feature.align === 'left' ? 'pr-12' : 'pl-12'}`}>
                      <motion.div
                        className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-shadow"
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6, delay: idx * 0.12 + 0.1 }}
                        viewport={{ once: true }}
                        whileHover={{ y: -4, scale: 1.02 }}
                      >
                        <motion.h3
                          className="text-lg font-bold text-gray-900 mb-2"
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          transition={{ duration: 0.6, delay: idx * 0.12 + 0.2 }}
                          viewport={{ once: true }}
                        >
                          {feature.title}
                        </motion.h3>
                        <motion.p
                          className="text-gray-600 text-sm leading-relaxed"
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          transition={{ duration: 0.6, delay: idx * 0.12 + 0.3 }}
                          viewport={{ once: true }}
                        >
                          {feature.description}
                        </motion.p>
                      </motion.div>
                    </div>

                    {/* Timeline dot - positioned next to timeline */}
                    <motion.div
                      className={`w-6 h-6 rounded-full border-4 border-white ${feature.color} flex-shrink-0 flex items-center justify-center`}
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      transition={{ duration: 0.5, delay: 1.2 + idx * 0.12 + 0.15, ease: "backOut" }}
                      viewport={{ once: true }}
                      whileHover={{ scale: 1.3 }}
                    >
                      <motion.div
                        className={`w-2 h-2 rounded-full bg-white`}
                        animate={{ scale: [1, 1.5, 1] }}
                        transition={{ duration: 2, repeat: Infinity, delay: 1.2 + idx * 0.1 }}
                      />
                    </motion.div>

                    {/* Empty space for alignment */}
                    <div className="w-1/2" />
                  </motion.div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-red-700 text-white relative overflow-hidden">
        <motion.div
          className="absolute inset-0 opacity-20"
          animate={{
            backgroundPosition: ['0% 0%', '100% 100%'],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
          style={{
            backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.3) 0%, transparent 60%)',
            backgroundSize: '200% 200%',
          }}
        />
        <motion.div
          className="max-w-4xl mx-auto px-4 text-center relative z-10"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <motion.h2
            className="text-3xl font-bold mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            Transform Your Workflows with DeepFlow AI
          </motion.h2>
          <motion.p
            className="text-xl text-red-100 mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            50% cost reduction. 3-6 month ROI. Deploy in weeks with industry templates.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3, ease: "backOut" }}
            viewport={{ once: true }}
          >
            <motion.div
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                to="/contact"
                className="inline-block px-8 py-3 bg-white text-red-700 rounded-full font-bold hover:bg-red-50 transition-all"
              >
                Request Demo
              </Link>
            </motion.div>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
};

export default DeepFlow;
